'use strict';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg:              '#0d1117',
  reneBg:          '#1a2332',
  reneBorder:      '1px solid #2d3748',
  agentBg:         '#111827',
  agentBorder:     '1px solid #1e293b',
  forfatter:       '#64748b',
  tekst:           '#e2e8f0',
  timestamp:       '#4b5563',
  input:           '#1a1f2e',
  inputBorder:     '1px solid #2d4a7a',
  sendKnap:        '#2563eb',
  udfOerKnap:      '#16a34a',
  fejlAccent:      '#ef4444',
  handlingAccent:  '#3b82f6',
  statusAccent:    '#22c55e',
  infoAccent:      '#6b7280',
  spørgsmålAccent: '#a78bfa',
  divider:         'rgba(255,255,255,0.06)',
};

const UDFØR_NØGLEORD = ['udfør', 'kør det', 'go', 'start', 'eksekvér', 'kør'];

function erUdfør(tekst) {
  const t = tekst.toLowerCase().trim();
  return UDFØR_NØGLEORD.some(k => t === k || t.startsWith(k + ' ') || t.endsWith(' ' + k));
}

function typefarve(type) {
  switch (type) {
    case 'fejl':      return T.fejlAccent;
    case 'handling':  return T.handlingAccent;
    case 'status':    return T.statusAccent;
    case 'spørgsmål': return T.spørgsmålAccent;
    default:          return T.infoAccent;
  }
}

function typelabel(type) {
  switch (type) {
    case 'fejl':      return 'FEJL';
    case 'handling':  return 'HANDLING';
    case 'status':    return 'STATUS';
    case 'spørgsmål': return 'SPØRGSMÅL';
    default:          return null;
  }
}

function fmtTid(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('da-DK', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
}

function erBillede(mime) {
  return mime && (mime.startsWith('image/') || mime === 'image/svg+xml');
}

function filStoerrelse(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Besked({ msg }) {
  const erRene = msg.forfatter === 'rene';
  const erFil  = msg.besked_type === 'fil';
  const accent = typefarve(msg.besked_type);
  const badge  = erFil ? null : typelabel(msg.besked_type);

  const filIndhold = useMemo(() => {
    if (!erFil || !msg.fil_url) return null;
    if (erBillede(msg.fil_type)) {
      return (
        <a href={msg.fil_url} target="_blank" rel="noopener noreferrer">
          <img src={msg.fil_url} alt={msg.fil_navn}
            style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 6,
              display: 'block', marginTop: 4, cursor: 'pointer' }} />
        </a>
      );
    }
    return (
      <a href={msg.fil_url} target="_blank" rel="noopener noreferrer"
        style={{ display: 'flex', alignItems: 'center', gap: 8,
          color: '#93c5fd', fontSize: 13, textDecoration: 'none',
          background: 'rgba(147,197,253,0.08)', border: '1px solid rgba(147,197,253,0.2)',
          borderRadius: 7, padding: '7px 11px', marginTop: 4 }}>
        <span style={{ fontSize: 18 }}>📎</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {msg.fil_navn}
        </span>
        {msg.fil_stoerrelse && (
          <span style={{ fontSize: 11, color: T.timestamp, flexShrink: 0 }}>
            {filStoerrelse(msg.fil_stoerrelse)}
          </span>
        )}
      </a>
    );
  }, [erFil, msg.fil_url, msg.fil_type, msg.fil_navn, msg.fil_stoerrelse]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column',
      alignItems: erRene ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3,
        flexDirection: erRene ? 'row-reverse' : 'row' }}>
        <span style={{ fontSize: 13, fontWeight: 600,
          color: erRene ? '#93c5fd' : accent }}>
          {erRene ? 'René' : msg.forfatter}
        </span>
        {badge && (
          <span style={{ fontSize: 9, fontWeight: 700, color: accent,
            background: accent + '22', border: `1px solid ${accent}55`,
            borderRadius: 4, padding: '1px 5px', letterSpacing: 0.6 }}>
            {badge}
          </span>
        )}
        <span style={{ fontSize: 11, color: T.timestamp }}>{fmtTid(msg.oprettet_kl)}</span>
      </div>
      <div style={{
        maxWidth: '82%', background: erRene ? T.reneBg : T.agentBg,
        border: erRene ? T.reneBorder : T.agentBorder,
        borderLeft:  erRene ? undefined : `3px solid ${accent}`,
        borderRight: erRene ? `3px solid #3b82f6` : undefined,
        borderRadius: erRene ? '12px 2px 12px 12px' : '2px 12px 12px 12px',
        padding: '9px 13px',
      }}>
        {erFil ? filIndhold : (
          <span style={{ fontSize: 14, color: T.tekst, lineHeight: 1.55,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {msg.besked}
          </span>
        )}
      </div>
    </div>
  );
}

const STATUS_FARVE = {
  KOERER: '#22c55e', AFVENTER: '#f59e0b', AFVENTER_GODKENDELSE: '#a78bfa',
  GODKENDT: '#3b82f6', FULDFOERT: '#475569', FEJLET: '#ef4444',
  ANNULLERET: '#374151', AFVIST: '#6b7280',
};

export default function OpgaveTrad({ opgaveId, titel, opgave }) {
  const [beskeder,   setBeskeder]   = useState([]);
  const [input,      setInput]      = useState('');
  const [sender,     setSender]     = useState(false);
  const [fejl,       setFejl]       = useState(null);
  const [indlæser,   setIndlæser]   = useState(true);
  const [uploader,   setUploader]   = useState(false);
  const bundRef  = useRef(null);
  const sseRef   = useRef(null);
  const filRef   = useRef(null);

  // ─── Initial load ───────────────────────────────────────────────────────────
  const hentTrad = useCallback(async () => {
    setIndlæser(true);
    try {
      const res = await fetch(`/api/os/opgaver/${opgaveId}/trad`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setBeskeder(await res.json());
      setFejl(null);
    } catch (err) {
      setFejl('Kunne ikke hente tråd: ' + err.message);
    } finally {
      setIndlæser(false);
    }
  }, [opgaveId]);

  // ─── SSE — live push fra server ─────────────────────────────────────────────
  useEffect(() => {
    hentTrad();
    const es = new EventSource(`/api/os/opgaver/${opgaveId}/trad/events`, { withCredentials: true });
    sseRef.current = es;
    es.addEventListener('message', e => {
      try {
        const ny = JSON.parse(e.data);
        setBeskeder(prev => {
          if (prev.some(m => m.id === ny.id)) return prev;
          return [...prev, ny];
        });
      } catch (_) {}
    });
    es.addEventListener('error', () => {
      // SSE genopkobler automatisk — ingen handling nødvendig
    });
    return () => { es.close(); sseRef.current = null; };
  }, [opgaveId, hentTrad]);

  // ─── Auto-scroll ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (bundRef.current) bundRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [beskeder]);

  // ─── Send ─────────────────────────────────────────────────────────────────────
  const sendBesked = async () => {
    const tekst = input.trim();
    if (!tekst || sender) return;
    setSender(true); setFejl(null);
    try {
      const res = await fetch(`/api/os/opgaver/${opgaveId}/trad`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ besked: tekst }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.fejl || `HTTP ${res.status}`);
      }
      setInput('');
    } catch (err) {
      setFejl('Kunne ikke sende: ' + err.message);
    } finally {
      setSender(false);
    }
  };

  const uploadFil = async (fil) => {
    if (!fil || uploader) return;
    setUploader(true); setFejl(null);
    try {
      const form = new FormData();
      form.append('fil', fil);
      const res = await fetch(`/api/os/opgaver/${opgaveId}/trad/upload`, {
        method: 'POST', credentials: 'include', body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.fejl || `HTTP ${res.status}`);
      }
    } catch (err) {
      setFejl('Fil-upload fejlede: ' + err.message);
    } finally {
      setUploader(false);
      if (filRef.current) filRef.current.value = '';
    }
  };

  const erUdførBesked = erUdfør(input);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%',
      background: T.bg, borderRadius: 12, overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {/* Header */}
      <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.divider}`,
        background: '#0a0f1e', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <div style={{ fontSize: 11, color: T.forfatter, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Opgave-tråd
          </div>
          {opgave?.afdeling && (
            <span style={{ fontSize: 10, color: T.forfatter }}>{opgave.afdeling}</span>
          )}
          {opgave?.status && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 0.6,
              color: STATUS_FARVE[opgave.status] || '#6b7280',
              background: (STATUS_FARVE[opgave.status] || '#6b7280') + '22',
              border: `1px solid ${(STATUS_FARVE[opgave.status] || '#6b7280')}55`,
              borderRadius: 4, padding: '1px 6px', textTransform: 'uppercase',
            }}>{opgave.status?.replace(/_/g, ' ')}</span>
          )}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.tekst,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
          {titel || 'Ukendt opgave'}
        </div>
        {opgave?.beskrivelse && (
          <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.45,
            maxHeight: 48, overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
            {opgave.beskrivelse}
          </div>
        )}
        {opgave?.fejlbesked && (
          <div style={{ marginTop: 6, fontSize: 12, color: T.fejlAccent,
            background: T.fejlAccent + '14', border: `1px solid ${T.fejlAccent}44`,
            borderRadius: 6, padding: '5px 10px', lineHeight: 1.4 }}>
            <strong>Fejl:</strong> {opgave.fejlbesked}
          </div>
        )}
        {opgave?.resultat && opgave.status === 'FULDFOERT' && (
          <div style={{ marginTop: 4, fontSize: 11, color: '#22c55e', opacity: 0.8 }}>
            Fuldført {fmtTid(opgave.afsluttet_kl)}
          </div>
        )}
      </div>

      {/* Beskeder */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px 8px' }}>
        {indlæser && (
          <div style={{ color: T.forfatter, fontSize: 13, textAlign: 'center', marginTop: 30 }}>
            Indlæser…
          </div>
        )}
        {!indlæser && beskeder.length === 0 && (
          <div style={{ color: T.forfatter, fontSize: 13, textAlign: 'center',
            marginTop: 30, fontStyle: 'italic' }}>
            Ingen beskeder endnu — skriv den første herunder.
          </div>
        )}
        {beskeder.map((msg, i) => <Besked key={msg.id || i} msg={msg} />)}
        {fejl && (
          <div style={{ background: T.fejlAccent + '18', border: `1px solid ${T.fejlAccent}44`,
            borderRadius: 7, color: T.fejlAccent, fontSize: 12, padding: '7px 11px', marginTop: 8 }}>
            {fejl}
          </div>
        )}
        <div ref={bundRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '10px 18px 14px', borderTop: `1px solid ${T.divider}`,
        background: '#0a0f1e', flexShrink: 0 }}>
        {erUdførBesked && (
          <div style={{ fontSize: 11, color: T.statusAccent, marginBottom: 6,
            background: T.statusAccent + '18', border: `1px solid ${T.statusAccent}44`,
            borderRadius: 6, padding: '4px 10px' }}>
            ⚡ Dette vil starte agenteksekvering
          </div>
        )}
        {/* Skjult fil-input */}
        <input
          ref={filRef}
          type="file"
          accept="image/*,.pdf,.docx,.txt,.csv"
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) uploadFil(e.target.files[0]); }}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          {/* Vedhæft-knap */}
          <button
            onClick={() => filRef.current?.click()}
            disabled={uploader}
            title="Vedhæft fil eller screenshot"
            style={{
              background: '#1e293b', border: '1px solid #2d4a7a',
              borderRadius: 8, color: uploader ? '#4b5563' : '#94a3b8',
              cursor: uploader ? 'not-allowed' : 'pointer',
              fontSize: 17, padding: '7px 10px', flexShrink: 0,
              transition: 'color 0.15s',
            }}
          >
            {uploader ? '⏳' : '📎'}
          </button>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendBesked(); }
            }}
            placeholder="Skriv til agenten… 'udfør' starter eksekvering (Enter sender)"
            rows={2}
            style={{ flex: 1, background: T.input, border: erUdførBesked
              ? `1px solid ${T.statusAccent}88` : T.inputBorder,
              borderRadius: 8, color: T.tekst, fontSize: 13, padding: '8px 11px',
              resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5 }}
          />
          <button
            onClick={sendBesked}
            disabled={!input.trim() || sender}
            style={{
              background: !input.trim() || sender ? '#1e3a5f'
                : erUdførBesked ? T.udfOerKnap : T.sendKnap,
              border: 'none', borderRadius: 8,
              color: !input.trim() || sender ? '#4b5563' : '#fff',
              cursor: !input.trim() || sender ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 600, padding: '9px 16px',
              whiteSpace: 'nowrap', transition: 'background 0.15s',
            }}
          >
            {sender ? 'Sender…' : erUdførBesked ? 'Udfør' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
